/**
 * React Query hook + helpers for Reports > Unposted Activity.
 *
 *   useUnpostedActivity(filters, enabled)  GET /v1/retail/unposted
 *   downloadUnpostedXlsx(filters)          GET /v1/retail/unposted/export
 *
 * What CounterPoint is still holding back from posting: register drawers opened
 * before today, and unposted inventory, purchasing and transfer documents. The
 * api returns both tables already filtered, so the page renders them as given
 * (sorting is internal to the shared screen).
 *
 * Contract:
 *
 *   GET /v1/retail/unposted
 *     ?store_no=...            optional, repeatable (a filter, never a scope)
 *     &type=...                optional, repeatable CounterPoint transaction type
 *     &nonpositive_only=true   optional, activity rows with a quantity of zero or below
 *   -> { as_of, scope: 'fleet' | 'store', drawers: [...], activity: [...], types: [...] }
 *
 * The row types come from `@ampersandbrands/ui`, because the shared screen owns
 * the wire shape. The daily email goes through the generic `useReportSubscriptions`
 * hook; what lives here is the pure pair that decides which filters a subscription
 * saves. Same 404/503 "not deployed yet" convention as the other report hooks.
 */

import { useQuery } from '@tanstack/react-query';
import type {
  UnpostedActivityFilters,
  UnpostedActivityRow,
  UnpostedDrawerRow,
} from '@ampersandbrands/ui';
import { apiGet } from './api';
import { RETAIL_API_BASE } from './useRetailStores';

export const UNPOSTED_API_BASE = `${RETAIL_API_BASE}/unposted`;

export const EMPTY_UNPOSTED_FILTERS: UnpostedActivityFilters = {
  store_no: [],
  type: [],
  nonpositive_only: false,
};

/**
 * The transaction types the QuickSight Unposted Activity table was filtered to.
 * Kept here so the page can offer the old view in one click and the Excel/email
 * numbers can be matched against QuickSight day for day.
 */
export const QUICKSIGHT_UNPOSTED_TYPES = [
  'Inventory Adjustment',
  'Item Merge',
  'Physical Count',
  'Purchase Request',
  'Quick Transfer',
  'Receiving',
  'Unsettled Credit Cards',
] as const;

export type UnpostedActivityResponse = {
  as_of: string | null;
  scope: 'fleet' | 'store';
  drawers: UnpostedDrawerRow[];
  activity: UnpostedActivityRow[];
  types: string[];
};

/** Query string for the report and its export. Lists repeat their key. */
export function buildUnpostedQs(filters: UnpostedActivityFilters): string {
  const sp = new URLSearchParams();
  for (const s of filters.store_no) sp.append('store_no', s);
  for (const t of filters.type) sp.append('type', t);
  if (filters.nonpositive_only) sp.set('nonpositive_only', 'true');
  return sp.toString();
}

/** Read the filters back out of the page's URL. */
export function parseUnpostedFilters(sp: URLSearchParams): UnpostedActivityFilters {
  return {
    store_no: sp.getAll('store_no').filter(Boolean),
    type: sp.getAll('type').filter(Boolean),
    nonpositive_only: sp.get('nonpositive_only') === '1' || sp.get('nonpositive_only') === 'true',
  };
}

/** Write the filters into the page's URL, leaving defaults out. */
export function writeUnpostedFilters(sp: URLSearchParams, filters: UnpostedActivityFilters): void {
  sp.delete('store_no');
  sp.delete('type');
  sp.delete('nonpositive_only');
  for (const s of filters.store_no) sp.append('store_no', s);
  for (const t of filters.type) sp.append('type', t);
  if (filters.nonpositive_only) sp.set('nonpositive_only', '1');
}

/**
 * What a subscription saves. The two lists are joined into comma strings because
 * the api keeps scalar filter values only. `nonpositive_only` is saved only when
 * on, since an unchecked box is not a filter.
 */
export function unpostedSubscriptionFilters(f: UnpostedActivityFilters): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (f.store_no.length > 0) out.store_no = f.store_no.join(',');
  if (f.type.length > 0) out.type = f.type.join(',');
  if (f.nonpositive_only) out.nonpositive_only = true;
  return out;
}

function arr<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

/** Tolerant read, so a partial or absent payload degrades to an empty report rather than a crash. */
export function normalizeUnposted(raw: unknown): UnpostedActivityResponse {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    as_of: typeof r.as_of === 'string' ? r.as_of : null,
    scope: r.scope === 'store' ? 'store' : 'fleet',
    drawers: arr<UnpostedDrawerRow>(r.drawers),
    activity: arr<UnpostedActivityRow>(r.activity),
    types: arr<string>(r.types).filter((t) => typeof t === 'string'),
  };
}

export function useUnpostedActivity(filters: UnpostedActivityFilters, enabled: boolean) {
  const query = useQuery<UnpostedActivityResponse, Error>({
    queryKey: ['retail', 'unposted', filters],
    queryFn: async () => {
      const qs = buildUnpostedQs(filters);
      return normalizeUnposted(await apiGet<unknown>(qs ? `${UNPOSTED_API_BASE}?${qs}` : UNPOSTED_API_BASE));
    },
    // The mirror refreshes every 5 minutes; a minute of cache is plenty.
    staleTime: 60_000,
    retry: false,
    enabled,
  });
  const data = query.data;
  return {
    asOf: data?.as_of ?? null,
    scope: data?.scope ?? 'fleet',
    drawers: data?.drawers ?? [],
    activity: data?.activity ?? [],
    types: data?.types ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

/** Browser download of the Excel export, following the filters on screen. */
export async function downloadUnpostedXlsx(filters: UnpostedActivityFilters): Promise<void> {
  const qs = buildUnpostedQs(filters);
  const res = await fetch(`${UNPOSTED_API_BASE}/export${qs ? `?${qs}` : ''}`, { credentials: 'include' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Export failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const m = (res.headers.get('content-disposition') ?? '').match(/filename="([^"]+)"/);
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = m?.[1] ?? 'unposted-activity.xlsx';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}
